import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./PgOwnerRegistrationOtp.css";
import API from "./config/API";

const PgOwnerRegistrationOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const pgOwnerEmailId = location.state?.pgOwnerEmailId || "";
  const mobileNumber = location.state?.mobileNumber || "";

  const [otp, setOtp] = useState("");

  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // ==========================================
  // 5 MINUTES = 300 SECONDS
  // ==========================================
  const OTP_VALID_TIME = 5 * 60;

  const [remainingSeconds, setRemainingSeconds] = useState(OTP_VALID_TIME);

  const [otpExpired, setOtpExpired] = useState(false);

  // ==========================================
  // INITIAL PAGE VALIDATION
  // ==========================================
  useEffect(() => {
    if (!pgOwnerEmailId && !mobileNumber) {
      navigate("/pgOwnerRegistration", {
        replace: true,
      });
    }
  }, [pgOwnerEmailId, mobileNumber, navigate]);

  // ==========================================
  // 5 MINUTE OTP TIMER
  // ==========================================
  useEffect(() => {
    if (remainingSeconds <= 0) {
      setOtpExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setOtpExpired(true);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [remainingSeconds]);

  // ==========================================
  // FORMAT TIMER
  // ==========================================
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0",
    )}`;
  };

  // ==========================================
  // OTP CHANGE
  // ==========================================
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");

    if (value.length <= 6) {
      setOtp(value);
    }

    if (message) {
      setMessage("");
      setMessageType("");
    }
  };

  // ==========================================
  // VERIFY OTP
  // ==========================================
  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (otpExpired) {
      setMessage(
        "OTP has expired. Please click Resend OTP to receive a new OTP.",
      );
      setMessageType("error");
      return;
    }

    if (!otp) {
      setMessage("Please enter OTP.");
      setMessageType("error");
      return;
    }

    if (otp.length !== 6) {
      setMessage("Please enter a valid 6-digit OTP.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const payload = {
        otp: Number(otp),
        pgownerEmailId: pgOwnerEmailId || null,
        mobileNumber: mobileNumber ? Number(mobileNumber) : null,
      };

      console.log("Verify OTP Payload:", payload);

      const response = await fetch(
        `${API.V2}/usersDashboard/verifyPgOwnerLoginOtp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      console.log("Verify OTP Response:", data);

      if (response.ok && data?.statusCode === 200) {
        setMessage(
          data?.message || "OTP verified successfully. Registration completed.",
        );

        setMessageType("success");

        setTimeout(() => {
          navigate("/", {
            replace: true,
          });
        }, 1500);
      } else {
        setMessage(
          data?.message || "Invalid or expired OTP. Please try again.",
        );

        setMessageType("error");
      }
    } catch (error) {
      console.error("OTP Verification Error:", error);

      setMessage("Unable to connect to server. Please try again later.");

      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // RESEND OTP
  // ==========================================
  const handleResendOtp = async () => {
    if (resendLoading) {
      return;
    }

    setResendLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const payload = {
        emailId: pgOwnerEmailId || "",
        customId: "",
        contactNo: mobileNumber ? Number(mobileNumber) : 0,
      };

      console.log("Resend OTP Payload:", payload);

      const response = await fetch(`${API.V2}/pgOwner/resendOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      console.log("Resend OTP Response:", data);

      if (response.ok && data?.statusCode === 200) {
        // Clear previous OTP
        setOtp("");

        // Restart 5-minute timer
        setRemainingSeconds(OTP_VALID_TIME);

        // Mark OTP as active
        setOtpExpired(false);

        setMessage(data?.message || "OTP resent successfully");

        setMessageType("success");
      } else {
        setMessage(data?.message || "Unable to resend OTP. Please try again.");

        setMessageType("error");
      }
    } catch (error) {
      console.error("Resend OTP Error:", error);

      setMessage("Unable to connect to server. Please try again later.");

      setMessageType("error");
    } finally {
      setResendLoading(false);
    }
  };

  // ==========================================
  // BACK
  // ==========================================
  const handleBack = () => {
    navigate("/pgOwnerRegistration");
  };

  return (
    <div className="pg-owner-otp-page">
      <div className="pg-owner-otp-card">
        <div className="otp-icon">🔐</div>

        <div className="otp-header">
          <h1>Verify OTP</h1>

          <p>
            Enter the 6-digit OTP sent to your registered email address or
            mobile number.
          </p>
        </div>

        {/* ======================================
            REGISTERED DETAILS
        ====================================== */}

        <div className="registered-details">
          {pgOwnerEmailId && (
            <div className="registered-item">
              <span className="detail-icon">📧</span>

              <div>
                <small>Email Address</small>

                <strong>{pgOwnerEmailId}</strong>
              </div>
            </div>
          )}

          {mobileNumber && (
            <div className="registered-item">
              <span className="detail-icon">📱</span>

              <div>
                <small>Mobile Number</small>

                <strong>{mobileNumber}</strong>
              </div>
            </div>
          )}
        </div>

        {/* ======================================
            OTP FORM
        ====================================== */}

        <form onSubmit={handleVerifyOtp}>
          <div className="otp-input-group">
            <label>
              Enter OTP <span>*</span>
            </label>

            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={handleOtpChange}
              placeholder="Enter 6-digit OTP"
              maxLength={6}
              autoFocus
              disabled={loading}
            />

            {/* ==================================
                TIMER
            ================================== */}

            {!otpExpired ? (
              <div className="otp-timer">
                OTP expires in <strong>{formatTime(remainingSeconds)}</strong>
              </div>
            ) : (
              <div className="otp-expired">
                OTP has expired. Please resend OTP.
              </div>
            )}
          </div>

          {/* ======================================
              MESSAGE
          ====================================== */}

          {message && (
            <div
              className={`otp-message ${
                messageType === "success"
                  ? "otp-success-message"
                  : "otp-error-message"
              }`}
            >
              {message}
            </div>
          )}

          {/* ======================================
              VERIFY BUTTON
          ====================================== */}

          <button
            type="submit"
            className="verify-otp-button"
            disabled={loading || otp.length !== 6 || otpExpired}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>

          {/* ======================================
              RESEND OTP
          ====================================== */}

          <button
            type="button"
            className="resend-otp-button"
            onClick={handleResendOtp}
            disabled={resendLoading}
          >
            {resendLoading ? "Sending OTP..." : "↻ Resend OTP"}
          </button>
        </form>

        {/* ======================================
            BACK BUTTON
        ====================================== */}

        <button
          type="button"
          className="back-registration-button"
          onClick={handleBack}
          disabled={loading || resendLoading}
        >
          ← Back to Registration
        </button>
      </div>
    </div>
  );
};

export default PgOwnerRegistrationOtp;
